import React, { useEffect, useState, useRef, useCallback } from 'react';
import { toast } from 'react-toastify';
import { QRCodeCanvas } from 'qrcode.react';
import api from '../utils/axiosConfig';
import { useAuth } from '../context/AuthContext';
import '../styles/AccountPage.css';

const CUSTOMER_APP_URL = process.env.REACT_APP_CUSTOMER_URL || (() => {
  const origin = window.location.origin;
  if (origin.includes('localhost')) return origin.replace(/:\d+$/, ':3000');
  return origin.replace('://bo.', '://');
})();

export default function AccountPage() {
  const { setAccount } = useAuth();
  const [form, setForm] = useState({
    title: '',
    description: '',
    address: '',
    phone: '',
    whatsapp: '',
    facebook: '',
    instagram: '',
    tiktok: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [accountId, setAccountId] = useState(null);
  const qrRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/backoffice/account');
        if (data.success) {
          const a = data.account;
          setAccountId(a.id);
          setForm({
            title: a.title || '',
            description: a.description || '',
            address: a.address || '',
            phone: a.phone || '',
            whatsapp: a.whatsapp || '',
            facebook: a.facebook || '',
            instagram: a.instagram || '',
            tiktok: a.tiktok || '',
          });
        }
      } catch (err) {
        toast.error('خطأ في تحميل بيانات الحساب');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put('/backoffice/account', form);
      if (data.success) {
        toast.success(data.message);
        setAccount(data.account);
      } else {
        toast.error(data.message);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'حدث خطأ');
    } finally {
      setSaving(false);
    }
  };

  const salonUrl = accountId
    ? `${CUSTOMER_APP_URL}/?businessid=${encodeURIComponent(accountId)}`
    : '';

  const handleShare = useCallback(async () => {
    if (!salonUrl) return;
    const shareData = {
      title: form.title || 'ادواري',
      text: `احجز موعدك في ${form.title || 'الصالون'} عبر ادواري`,
      url: salonUrl,
    };
    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        if (err.name !== 'AbortError') console.error(err);
      }
    } else {
      await navigator.clipboard.writeText(salonUrl);
      toast.success('تم نسخ الرابط');
    }
  }, [salonUrl, form.title]);

  const handleDownloadQR = useCallback(() => {
    const canvas = qrRef.current?.querySelector('canvas');
    if (!canvas) return;
    const link = document.createElement('a');
    link.download = `${form.title || 'salon'}-qr.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  }, [form.title]);

  const handleCopyLink = useCallback(async () => {
    if (!salonUrl) return;
    await navigator.clipboard.writeText(salonUrl);
    toast.success('تم نسخ الرابط');
  }, [salonUrl]);

  if (loading) return <div className="page-loading">جاري التحميل...</div>;

  return (
    <div className="account-page">
      <h1>بيانات الحساب</h1>
      <form onSubmit={handleSubmit} className="form-card">
        <div className="form-group">
          <label>اسم الصالون</label>
          <input name="title" value={form.title} onChange={handleChange} required />
        </div>
        <div className="form-group">
          <label>الوصف</label>
          <textarea name="description" value={form.description} onChange={handleChange} rows={3} />
        </div>
        <div className="form-group">
          <label>العنوان</label>
          <input name="address" value={form.address} onChange={handleChange} />
        </div>
        <div className="form-group">
          <label>واتساب</label>
          <input name="whatsapp" value={form.whatsapp} onChange={handleChange} dir="ltr" />
        </div>

        <section className="social-section" aria-labelledby="social-section-title">
        <h2 id="social-section-title" className="form-section-title">وسائل التواصل الاجتماعي</h2>
        <p className="form-section-hint">اختياري — تظهر الروابط في صفحة الصالون عند تعبئتها</p>
        <div className="form-group">
          <label>فيسبوك</label>
          <input
            name="facebook"
            value={form.facebook}
            onChange={handleChange}
            dir="ltr"
            placeholder="https://facebook.com/..."
          />
        </div>
        <div className="form-group">
          <label>إنستغرام</label>
          <input
            name="instagram"
            value={form.instagram}
            onChange={handleChange}
            dir="ltr"
            placeholder="https://instagram.com/..."
          />
        </div>
        <div className="form-group">
          <label>تيك توك</label>
          <input
            name="tiktok"
            value={form.tiktok}
            onChange={handleChange}
            dir="ltr"
            placeholder="https://tiktok.com/@..."
          />
        </div>
        </section>

        <button type="submit" className="save-btn" disabled={saving}>
          {saving ? 'جاري الحفظ...' : 'حفظ التغييرات'}
        </button>
      </form>

      {salonUrl && (
        <section className="qr-section">
          <h2>رابط صفحة الصالون</h2>
          <div className="qr-card">
            <div className="qr-canvas-wrap" ref={qrRef}>
              <QRCodeCanvas
                value={salonUrl}
                size={200}
                level="H"
                includeMargin
                bgColor="#ffffff"
                fgColor="#1a1a2e"
              />
            </div>
            <p className="qr-salon-name">{form.title}</p>
            <div className="qr-link-display" dir="ltr">{salonUrl}</div>
            <div className="qr-actions">
              <button type="button" className="qr-action-btn qr-share-btn" onClick={handleShare}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>
                مشاركة
              </button>
              <button type="button" className="qr-action-btn qr-copy-btn" onClick={handleCopyLink}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
                نسخ الرابط
              </button>
              <button type="button" className="qr-action-btn qr-download-btn" onClick={handleDownloadQR}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                تحميل QR
              </button>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
